import styled from 'styled-components';
import { theme } from '../theme';

// Table Container
export const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.light};
  background: ${theme.colors.background.primary};
`;

// Table
export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${theme.typography.fontSize.sm};
`;

// Table Header
export const TableHeader = styled.thead`
  background-color: ${theme.colors.gray[50]};
`;

export const TableHeaderRow = styled.tr`
  border-bottom: 1px solid ${theme.colors.border.light};
`;

export const TableHeaderCell = styled.th<{
  align?: 'left' | 'center' | 'right';
  width?: string;
  sortable?: boolean;
}>`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  text-align: ${props => props.align || 'left'};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  white-space: nowrap;
  ${props => props.width && `width: ${props.width};`}
  
  ${props => props.sortable && `
    cursor: pointer;
    user-select: none;
    transition: background-color ${theme.transitions.fast};
    
    &:hover {
      background-color: ${theme.colors.gray[100]};
    }
  `}
`;

// Table Body
export const TableBody = styled.tbody``;

export const TableRow = styled.tr<{
  hoverable?: boolean;
  selected?: boolean;
  clickable?: boolean;
}>`
  border-bottom: 1px solid ${theme.colors.border.light};
  
  &:last-child {
    border-bottom: none;
  }
  
  ${props => props.selected && `
    background-color: ${theme.colors.primary[50]};
  `}
  
  ${props => (props.hoverable || props.clickable) && `
    transition: background-color ${theme.transitions.fast};
    
    &:hover {
      background-color: ${theme.colors.gray[50]};
    }
  `}
  
  ${props => props.clickable && `
    cursor: pointer;
  `}
`;

export const TableCell = styled.td<{
  align?: 'left' | 'center' | 'right';
  width?: string;
}>`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  text-align: ${props => props.align || 'left'};
  color: ${theme.colors.text.primary};
  vertical-align: middle;
  ${props => props.width && `width: ${props.width};`}
`;

// Table Actions
export const TableActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

export const TableActionButton = styled.button<{
  variant?: 'view' | 'edit' | 'delete';
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: ${theme.borderRadius.base};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  
  ${props => {
    switch (props.variant) {
      case 'view':
        return `
          background-color: ${theme.colors.primary[100]};
          color: ${theme.colors.primary[600]};
          
          &:hover {
            background-color: ${theme.colors.primary[200]};
          }
        `;
      case 'edit':
        return `
          background-color: ${theme.colors.warning[100]};
          color: ${theme.colors.warning[600]};
          
          &:hover {
            background-color: ${theme.colors.warning[200]};
          }
        `;
      case 'delete':
        return `
          background-color: ${theme.colors.error[100]};
          color: ${theme.colors.error[600]};
          
          &:hover {
            background-color: ${theme.colors.error[200]};
          }
        `;
      default:
        return `
          background-color: ${theme.colors.gray[100]};
          color: ${theme.colors.gray[600]};
          
          &:hover {
            background-color: ${theme.colors.gray[200]};
          }
        `;
    }
  }}
  
  &:focus {
    outline: 2px solid ${theme.colors.primary[500]};
    outline-offset: 2px;
  }
`;

// Table Pagination
export const TablePagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: between;
  padding: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.border.light};
  background-color: ${theme.colors.gray[50]};
`;

export const PaginationInfo = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
`;

export const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-left: auto;
`;

export const PaginationButton = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 ${theme.spacing[2]};
  border: 1px solid ${theme.colors.border.medium};
  border-radius: ${theme.borderRadius.base};
  background-color: ${props => props.active ? theme.colors.primary[500] : theme.colors.background.primary};
  color: ${props => props.active ? theme.colors.text.inverse : theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.active ? theme.colors.primary[600] : theme.colors.gray[100]};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:focus {
    outline: 2px solid ${theme.colors.primary[500]};
    outline-offset: 2px;
  }
`;

// Table Empty State
export const TableEmptyState = styled.div`
  padding: ${theme.spacing[12]} ${theme.spacing[4]};
  text-align: center;
  color: ${theme.colors.text.secondary};
`;

export const TableEmptyIcon = styled.div`
  font-size: ${theme.typography.fontSize['4xl']};
  margin-bottom: ${theme.spacing[4]};
  opacity: 0.5;
`;

export const TableEmptyText = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  margin: 0;
`;

// Table Filters
export const TableFilters = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[4]};
  background-color: ${theme.colors.gray[50]};
  border-bottom: 1px solid ${theme.colors.border.light};
  flex-wrap: wrap;
`;

export const TableFilter = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

export const TableFilterLabel = styled.label`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

// Table Loading
export const TableLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]};
  color: ${theme.colors.text.secondary};
`;

// Table Selection
export const TableSelectAll = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  accent-color: ${theme.colors.primary[500]};
  cursor: pointer;
`;

export const TableSelectRow = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  accent-color: ${theme.colors.primary[500]};
  cursor: pointer;
`;

// Responsive Table
export const ResponsiveTable = styled.div`
  @media (max-width: ${theme.breakpoints.md}) {
    ${Table} {
      display: none;
    }
  }
`;

export const MobileTableCard = styled.div`
  display: none;
  
  @media (max-width: ${theme.breakpoints.md}) {
    display: block;
    background: ${theme.colors.background.primary};
    border: 1px solid ${theme.colors.border.light};
    border-radius: ${theme.borderRadius.lg};
    padding: ${theme.spacing[4]};
    margin-bottom: ${theme.spacing[4]};
  }
`;

export const MobileTableRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing[2]} 0;
  
  &:not(:last-child) {
    border-bottom: 1px solid ${theme.colors.border.light};
  }
`;

export const MobileTableLabel = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.secondary};
`;

export const MobileTableValue = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
`;
